import { forwardRef, useState, useImperativeHandle, useEffect } from "react";
import { Empty, Table, Flex, Input, Pagination } from "antd";
import LoadingOrErrorPanel from "components/LoadingOrErrorPanel";

const PortalTable = forwardRef(({content, initialValuesSelectedRows = [], ...props}, ref) => {
    const [dadosCarregados, setDadosCarregados] = useState([]);
    const [dadosSelecionados, setDadosSelecionados] = useState([]);
    const [paginasArmazenadas, setPaginasArmazenadas] = useState({});
    const [loading, setLoading] = useState(false);
    const [erro, setErro] = useState({temErro: false, mensagem: ''});
    const [filtroAplicado, setFiltroAplicado] = useState("");
    const { Search } = Input;

    const [callbackOpcoes, setCallbackOpcoes] = useState({
        total: 10,
        tamanhoPagina: 10,
        paginaAtual: 1,
        skipToken: '',
        filtro: ''
    });

    useEffect(()=>{
        handleAlteracaoDePaginacao(1, callbackOpcoes.tamanhoPagina);
        setDadosSelecionados(initialValuesSelectedRows);
    }, [])
    
    useEffect(() => {
        setDadosSelecionados(initialValuesSelectedRows);
    }, [initialValuesSelectedRows]);      

    useImperativeHandle(ref, () => ({
        getSelected,
        clearSelected,
        setSelectedPage
    }));

    const getSelected = () => {
        return dadosSelecionados;
    }

    const clearSelected = () => {
        setDadosSelecionados([]);
        return [];
    }
    const setSelectedPage = (newPage) => {
        handleAlteracaoDePaginacao(newPage, callbackOpcoes.tamanhoPagina);
    }

    const MapearRegistrosParaDataSource = (registros, campoIdDaListagem) => {
        return registros.map(obj => {
          const entries = Object.entries(obj);
          const [, keyValue] = entries.find(([key]) => key === campoIdDaListagem);
          const resto = Object.fromEntries(entries.filter(([key]) => key !== campoIdDaListagem));
    
          return { key: keyValue, ...resto };
        });
      }

    const BuscarDadosASeremAdicionados = async (pagina = 1, tamanhoPagina = 10, filtro = '', skipToken = '') =>{
        setLoading(true);
    
        const {callback = null, campoRegistrosDaListagem, campoIdDaListagem} = (content.adicionarDados || {})
        if(callback == null || typeof callback != "function" || loading == true) return;
    
        const dados = await callback(filtro, tamanhoPagina, skipToken)
              .catch(error =>{
                setErro({temErro: true, mensagem: error.response?.data?.erros[0] ?? 'Erro inesperado ao obter os dados'});
                setLoading(false);
                return;
            });
    
        if(dados == undefined) return;
    
        const registros = dados[campoRegistrosDaListagem];
    
        setCallbackOpcoes((prevCallbackOpcoes) =>({
            ...prevCallbackOpcoes,
            total: pagina == 1 ? dados.quantidadeRegistros : prevCallbackOpcoes.total,
            skipToken: dados.skipToken == null ? '' : dados.skipToken
        }));
    
        const dataSource = MapearRegistrosParaDataSource(registros, campoIdDaListagem);
        setDadosCarregados(dataSource);
        setPaginasArmazenadas((prevPaginasArmazenadas) => ({
            ...prevPaginasArmazenadas,
            [pagina]: [...dataSource],
        }));
    
        setLoading(false);
    }

    const handleAlteracaoDePaginacao = (paginaAtual, tamanhoPagina) =>{
        let tamanhoPaginaAtual = callbackOpcoes.tamanhoPagina;
        let skipToken = callbackOpcoes.skipToken;
    
        if (tamanhoPaginaAtual != tamanhoPagina) {
            skipToken = '';
            paginaAtual = 1;
            setPaginasArmazenadas({});
        }
    
        setCallbackOpcoes({
            ...callbackOpcoes,
            paginaAtual,
            tamanhoPagina,
            skipToken
        })
    
        if(paginasArmazenadas[paginaAtual] && tamanhoPaginaAtual == tamanhoPagina){
          setDadosCarregados(paginasArmazenadas[paginaAtual]);
        } else {
          BuscarDadosASeremAdicionados(paginaAtual, tamanhoPagina, filtroAplicado, skipToken);
        }
    }

    const rowSelection = {
        selectedRowKeys: dadosSelecionados.map(r => r.key),
        onSelect: (record, selected, selectedRows, nativeEvent) =>{
            if(selected){
                setDadosSelecionados(prev => [...prev, record]);
            }else{
                setDadosSelecionados(prev => prev.filter(x=>x.key != record.key));
            }
        },
        onSelectAll: (selected, selectedRows, changeRows) =>{
            setDadosSelecionados(prev =>{
                const changeKeys = new Set(changeRows.map(row => row.key));
                if(selected){
                    const existingKeys = new Set(prev.map(row => row.key));
                    const novosSelecionados = changeRows.filter(row => !existingKeys.has(row.key));
                    return [...prev, ...novosSelecionados];
                }else{
                    return prev.filter(row => !changeKeys.has(row.key));
                }
            });
        }
    };

    const handleFiltrar = async (valorFiltro) => {
        setPaginasArmazenadas({});
        setFiltroAplicado(valorFiltro);
        setCallbackOpcoes({
            ...callbackOpcoes,
            paginaAtual: 1
        });
      
        await BuscarDadosASeremAdicionados(1, callbackOpcoes.tamanhoPagina, valorFiltro);
    }


    return (
        <LoadingOrErrorPanel loading={loading} hasError={erro.temErro} mensagemErro={erro.mensagem}>
            <Flex justify='flex-end' >
                <Search
                    placeholder={props.adicionarDados?.placeholderFiltro ?? "Filtrar dados"}
                    onSearch={handleFiltrar}
                    allowClear
                    style={{width: 235}}
                />
            </Flex>
            <Table
                dataSource={dadosCarregados}
                columns={content.columns}
                rowSelection={rowSelection}
                pagination={false}
                tableLayout="auto"
                size="small"
                scroll= { dadosCarregados.length > 0 ? { y: 275 } : undefined }
                locale={{ emptyText: <Empty description={'Sem dados'} image={Empty.PRESENTED_IMAGE_SIMPLE}/> }}
            />
            <Pagination 
                defaultCurrent={1}
                simple={{ readOnly: true }}
                total={callbackOpcoes.total}
                showTotal={(total) => `Selecionados: ${dadosSelecionados.length} Total: ${total}`}
                pageSize={callbackOpcoes.tamanhoPagina}
                onChange={handleAlteracaoDePaginacao}
                disabled={loading}
                showSizeChanger={callbackOpcoes.total > 10}
                current={callbackOpcoes.paginaAtual}
                locale={{ items_per_page: '/ página' }}
            />
        </LoadingOrErrorPanel>
    )

})

export default PortalTable;